/**
 * Word Error Rate — the ASR speech-check harness's measurement core.
 *
 * WHY THIS EXISTS. Everything the judge scores sits on the interview transcript, and the
 * transcript comes from the browser's free Web Speech API. We measured judge fairness on CLEAN
 * TEXT and never measured whether Indian English on real phones survives transcription. This
 * module produces the number that decides go/no-go before the first pilot, so the maths must be
 * defensible — token-level Levenshtein with an exact backtrace, not an approximation.
 *
 * NORMALISATION PHILOSOPHY — measure the ENGINE, not orthography. A transcript that writes
 * "20 percent" for a reference "20%" is a CORRECT hearing; counting it as two errors would
 * blame the engine for a spelling convention. Each fold below exists because the engine and the
 * passage author can legitimately disagree on how to WRITE the same correctly-heard sound:
 *
 *   - digits ↔ number words ("20"/"twenty"): recognisers emit digits for spoken numbers; the
 *     canonical form here is WORDS, folded for 0–100 plus the exact round forms "100" and
 *     "1000" ("one hundred", "one thousand"). Larger numbers stay literal digits — "40000"
 *     could be "forty thousand" or "four zero zero zero zero", and guessing would flatter WER.
 *   - "%" ↔ "percent": no one says "percent-sign"; the symbol IS the word.
 *   - "₹" / "rs" ↔ "rupees": Indian passages will contain money; engines write any of the
 *     three for the same sound. NOTE: word ORDER is deliberately not fixed — "₹500" folds to
 *     "rupees 500" while a speaker may say "500 rupees"; that residual counts as errors.
 *   - "&" ↔ "and": the ampersand is read aloud as "and" ("R&D" aside — that residual counts).
 *   - "ok" ↔ "okay": same word, two accepted spellings, engines use both.
 *
 * The folding is deliberately CONSERVATIVE: only equivalences that are unambiguous in every
 * context are folded. Anything ambiguous ("2026" vs "twenty twenty six", plural "rupee",
 * "percentage") is left alone and counts as an error, so the WER reported here is an UPPER
 * BOUND on true engine error — the number can only be pessimistic, never flattered.
 *
 * Punctuation: apostrophes are DELETED ("don't" → "dont") so contraction spelling can't create
 * a phantom token boundary; every other punctuation mark becomes a space. Hyphens therefore
 * split ("twenty-one" → "twenty one"), which is exactly what makes "21" fold-equal to the
 * written compound.
 */
// ── number words (0–100 + exact round forms) ──────────────────────────────
const UNITS = [
    "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
    "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen",
    "eighteen", "nineteen",
];
const TENS = ["twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
/**
 * 0–100 → canonical English words ("21" → "twenty one" — hyphen-free, matching what the
 * punctuation pass does to a written "twenty-one"). Returns null outside the folded range.
 */
function numberToWords(n) {
    if (n < 0 || !Number.isInteger(n))
        return null;
    if (n < 20)
        return UNITS[n];
    if (n < 100) {
        const t = TENS[Math.floor(n / 10) - 2];
        return n % 10 === 0 ? t : `${t} ${UNITS[n % 10]}`;
    }
    if (n === 100)
        return "one hundred";
    if (n === 1000)
        return "one thousand";
    return null;
}
/**
 * Fold a single token to its canonical spoken form. Digit tokens fold only when they are the
 * canonical decimal spelling (no leading zeros): "07" is a spoken "oh seven"/"zero seven"
 * ambiguity, and folding it to "seven" would flatter the engine.
 */
function foldToken(tok) {
    if (tok === "ok")
        return "okay";
    if (tok === "rs")
        return "rupees";
    if (/^\d+$/.test(tok)) {
        const n = Number(tok);
        if (String(n) === tok) {
            const words = numberToWords(n);
            if (words !== null)
                return words;
        }
    }
    return tok;
}
/**
 * Canonicalise text to the form WER is computed over: lowercase, symbol folds, punctuation
 * stripped, whitespace collapsed, token folds applied. Exposed (not just internal) so the
 * owner console can render reference/transcript exactly as they were scored.
 */
export function normaliseSpeech(text) {
    const symbolFolded = text
        .toLowerCase()
        // DIGIT-GROUPING CANONICALISATION, before anything else touches punctuation. Without it a
        // CORRECTLY-heard read carried a ~13% error floor on the numbers passage: the reference
        // "48,500" split at the comma into "forty eight 500" while the engine wrote "48500" — three
        // edits for a perfect hearing, and whether a volunteer scored 0% or 13% became a lottery
        // over the engine's digit formatting, unrelated to hearing quality.
        .replace(/(\d),(?=\d)/g, "$1")
        // Symbol → word folds run BEFORE punctuation stripping, or the symbols would vanish.
        .replace(/%/g, " percent ")
        .replace(/₹/g, " rupees ")
        .replace(/&/g, " and ")
        // Apostrophes deleted, not spaced: "don't" must stay one token ("dont"), not become "don t".
        .replace(/['’]/g, "")
        // Everything that is not a letter, digit or whitespace becomes a token boundary.
        .replace(/[^a-z0-9\s]/g, " ");
    const merged = mergeDigitGroups(symbolFolded.split(/\s+/).filter(Boolean));
    return merged
        // A fold can EXPAND one token into several ("21" → "twenty one"), hence flatMap.
        .flatMap((tok) => foldToken(tok).split(" "))
        .join(" ");
}
/**
 * Merge ADJACENT all-digit tokens when both have 3+ digits — "98401 23456" and "9840123456" are
 * the same phone number spoken the same way, differing only in the engine's grouping. The 3-digit
 * floor is what keeps this from mangling genuine small numbers: "3 4 people" must never become
 * "34 people" (those tokens fold to words downstream anyway).
 */
function mergeDigitGroups(toks) {
    const out = [];
    for (const tok of toks) {
        const prev = out[out.length - 1];
        if (prev !== undefined && /^\d{3,}$/.test(prev) && /^\d{3,}$/.test(tok)) {
            out[out.length - 1] = prev + tok;
        }
        else {
            out.push(tok);
        }
    }
    return out;
}
const tokens = (text) => {
    const norm = normaliseSpeech(text);
    return norm === "" ? [] : norm.split(" ");
};
/**
 * Token-level Levenshtein WER with an exact backtrace, so substitutions/deletions/insertions
 * are the true counts of the minimal edit script — never approximated from the distance.
 *
 * PINNED CHOICES (tests hold these):
 *   - wer = (S+D+I) / max(refTokens, 1) and is NOT capped at 1.0. Insertions against a short
 *     reference can push WER above 1.0 — an engine that hallucinates twice as many words as
 *     were spoken is genuinely worse than "100% wrong", and capping would hide that. This is
 *     the standard uncapped definition, kept so the number stays comparable to literature.
 *   - empty transcript vs non-empty reference = 1.0 exactly (all deletions).
 *   - both empty = 0 (nothing was said, nothing was missed).
 *   - empty reference vs non-empty transcript: every token is an insertion and the denominator
 *     floor of 1 makes wer = insertions (no division by zero, still honest about hallucination).
 *   - backtrace tie-break prefers the diagonal (match/substitution) over deletion over
 *     insertion, so the S/D/I split is deterministic across runs.
 */
export function wordErrorRate(reference, transcript) {
    const ref = tokens(reference);
    const hyp = tokens(transcript);
    const m = ref.length;
    const n = hyp.length;
    // dp[i][j] = edit distance between ref[0..i) and hyp[0..j).
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = 1; i <= m; i++)
        dp[i][0] = i;
    for (let j = 1; j <= n; j++)
        dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            const diag = dp[i - 1][j - 1] + (ref[i - 1] === hyp[j - 1] ? 0 : 1);
            const del = dp[i - 1][j] + 1;
            const ins = dp[i][j - 1] + 1;
            dp[i][j] = Math.min(diag, del, ins);
        }
    }
    // Backtrace — pinned preference: diagonal, then deletion, then insertion.
    let substitutions = 0;
    let deletions = 0;
    let insertions = 0;
    let i = m;
    let j = n;
    while (i > 0 || j > 0) {
        if (i > 0 && j > 0) {
            const cost = ref[i - 1] === hyp[j - 1] ? 0 : 1;
            if (dp[i][j] === dp[i - 1][j - 1] + cost) {
                if (cost === 1)
                    substitutions++;
                i--;
                j--;
                continue;
            }
        }
        if (i > 0 && dp[i][j] === dp[i - 1][j] + 1) {
            deletions++;
            i--;
            continue;
        }
        insertions++;
        j--;
    }
    const errors = substitutions + deletions + insertions;
    return {
        wer: errors / Math.max(m, 1),
        substitutions,
        deletions,
        insertions,
        refTokens: m,
    };
}
//# sourceMappingURL=wer.js.map